import { Sides } from "@project/emerge-types";
import type { Vector3 } from "three";
import { PadSide } from "../enum/PadSide";
import { LTPartType } from "../enum/PartType";
import type { RectProperties } from "../interfaces/RectProperties";
import {
  createEqualPaddingMap,
  type PaddingMap,
} from "../user_options/InteriorOptions";
import { RectPropertiesBySide } from "./RectProperties";

function rectPropertiesForSide(side: Sides): RectProperties {
  const props = RectPropertiesBySide[side];
  if (props === undefined) {
    throw new Error(`RectPropertiesBySide: missing side ${String(side)}`);
  }
  return props;
}

export class PadHelper {
  private positions: Vector3[];
  private strength: number;
  private side: Sides;
  private padding: PaddingMap;
  private caseInteriorSize: Vector3;
  constructor(
    side: Sides,
    width: number,
    position: Vector3,
    caseInteriorSize: Vector3,
    padding: PaddingMap | number,
  ) {
    this.side = side;
    const normalizedPadding =
      typeof padding === "number" ? createEqualPaddingMap(padding) : padding;
    this.padding = normalizedPadding;
    this.strength = normalizedPadding[side];
    this.caseInteriorSize = caseInteriorSize;
    const { horizontalDir } = rectPropertiesForSide(side);
    this.positions = [
      position.clone().add(horizontalDir.clone().multiplyScalar(-width / 2)),
      position.clone().add(horizontalDir.clone().multiplyScalar(width / 2)),
    ];
  }
  public get center(): Vector3 {
    const left = this.positions[PadSide.Left];
    const right = this.positions[PadSide.Right];
    if (!left || !right) {
      throw new Error("PadHelper: positions not initialized");
    }
    return left.clone().lerp(right, 0.5);
  }
  public shrink(side: PadSide) {
    const { horizontalDir } = rectPropertiesForSide(this.side);
    let amount = 0;
    switch (this.side) {
      case Sides.LEFT:
        amount = this.padding[side === PadSide.Left ? Sides.BACK : Sides.FRONT];
        break;
      case Sides.RIGHT:
        amount = this.padding[side !== PadSide.Left ? Sides.BACK : Sides.FRONT];
        break;
      case Sides.BACK:
        amount = this.padding[side === PadSide.Left ? Sides.RIGHT : Sides.LEFT];
        break;
      case Sides.FRONT:
        amount = this.padding[side !== PadSide.Left ? Sides.RIGHT : Sides.LEFT];
        break;
    }
    const pos = this.positions[side];
    if (!pos) {
      throw new Error("PadHelper.shrink: invalid pad side");
    }
    pos.add(
      horizontalDir
        .clone()
        .multiplyScalar((side === PadSide.Left ? 1 : -1) * amount),
    );
  }
  public sideTouchesCaseOuter(side: PadSide): boolean {
    const { horizontal } = rectPropertiesForSide(this.side);
    const pos = this.positions[side];
    if (!pos) {
      throw new Error("PadHelper.sideTouchesCaseOuter: invalid pad side");
    }
    return Math.abs(pos[horizontal]) === this.caseInteriorSize[horizontal] / 2;
  }
  public get isAtCaseOuter(): boolean {
    const { normal } = rectPropertiesForSide(this.side);
    return Math.abs(this.center[normal]) === this.caseInteriorSize[normal] / 2;
  }
  public getProperties(height: number): {
    position: Vector3;
    size: [number, number];
    strength: number;
    type: LTPartType.Pad;
  } {
    const center = this.center;
    center.y = -this.caseInteriorSize.y / 2 + height / 2;
    const left = this.positions[PadSide.Left];
    const right = this.positions[PadSide.Right];
    if (!left || !right) {
      throw new Error("PadHelper.getProperties: positions not initialized");
    }
    return {
      position: center,
      size: [left.distanceTo(right), height],
      strength: this.strength,
      type: LTPartType.Pad,
    };
  }
}
